import { roundNumberToNDecimalPlaces } from '@app/utils/helpers';
import { itemsByRatingChartConfig as chartConfig } from '@shared/components/chart/configs';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import { CustomTooltipProps } from '@shared/components/chart/tooltips/types';
import Unit from '@shared/components/content/unit';
import { ClassValue } from 'clsx';
import { generatePath, useNavigate } from 'react-router-dom';
import { Sector } from 'recharts';
import { PieSectorDataItem } from 'recharts/types/polar/Pie';

interface ProductRatingsPieChartProps {
  className?: ClassValue;
  data: any;
  averageEmissionsCategory: string;
  collectionUuid?: string;
}

interface ProductRatingPieChartTooltipProps extends CustomTooltipProps {}

function ProductRatingPieChartTooltip({
  value,
  name,
  chartData,
}: ProductRatingPieChartTooltipProps) {
  const percent =
    ((value as number) /
      (chartData?.reduce((acc, item) => acc + (item['items'] as number), 0) ||
        1)) *
    100;

  return (
    <>
      <div
        className="size-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
        style={
          {
            '--color-bg': `var(--color-${name})`,
          } as React.CSSProperties
        }
      />
      {chartConfig[name as keyof typeof chartConfig]?.label || name}

      <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
        {roundNumberToNDecimalPlaces(value as number, 2, false)}
        <span className="ml-1 flex items-baseline font-normal  text-muted-foreground">
          ({roundNumberToNDecimalPlaces(percent, 0, false)}{' '}
          <Unit variant="%" className="" />)
        </span>
      </div>
    </>
  );
}

export default function ProductRatingsPieChart({
  data,
  averageEmissionsCategory,
  collectionUuid,
}: ProductRatingsPieChartProps) {
  const navigate = useNavigate();

  const renderActiveShape = ({
    outerRadius = 0,
    ...props
  }: PieSectorDataItem) => {
    return (
      <g>
        <Sector
          {...props}
          outerRadius={outerRadius + 5}
          className="transition-all duration-200 ease-in-out"
        />
      </g>
    );
  };

  const renderCustomLabel = ({
    x,
    y,
    name,
    fill,
    value,
  }: {
    x?: number | string;
    y: number | string;
    name: string;
    fill?: string;
    value: number;
  }) => {
    return (
      <text x={x} y={y} fill={fill} textAnchor="end" dominantBaseline="central">
        {value ? name : ''}
      </text>
    );
  };

  return (
    <SimplePieChart
      dataKey="items"
      nameKey="category"
      chartConfig={chartConfig}
      chartData={data.reverse()}
      innerRadius={87}
      productRatingCenter={true}
      centerLabel={{
        value: averageEmissionsCategory.toLocaleLowerCase(),
        label: 'Avg rating',
      }}
      className="size-72"
      onClick={(data) => {
        navigate(
          generatePath(
            collectionUuid
              ? `/collections/:collectionUuid/products?page=1&emissionsCategory=${data.payload.category}`
              : `/products?page=1&emissionsCategory=${data.payload.category}`,
            {
              collectionUuid: collectionUuid!,
            }
          )
        );
      }}
      activeShape={renderActiveShape}
      label={renderCustomLabel}
      tooltipFormatter={ProductRatingPieChartTooltip}
    />
  );
}
